import qs from 'qs';
import { captureException, setContext, setUser } from '@sentry/react';
import { RestApiError } from '../utils';
import { AuthUtils } from '@shared/utils';
import { HttpMethod, IBaseRequestParams, IRequestParams, type Headers } from '../models';

const IGNORED_STATUS_ERRORS = [401];

export class Api {
  private static readonly baseUrl = process.env.API_URL!;

  protected static get<Response>(request: IRequestParams<null>): Promise<Response> {
    const fullUrl = request.url + this._objectToQuery(request.queryParams);
    return this._wrapRequest(() =>
      this._request<void, Response>({
        method: HttpMethod.Get,
        url: fullUrl,
        headers: request.headers,
        signal: request.signal,
      }),
    );
  }

  protected static post<Request, Response>(request: IRequestParams<Request>): Promise<Response> {
    const fullUrl = request.url + this._objectToQuery(request.queryParams);
    return this._wrapRequest(() =>
      this._request<Request, Response>({
        method: HttpMethod.Post,
        url: fullUrl,
        data: request.data,
        headers: request.headers,
        signal: request.signal,
      }),
    );
  }

  protected static put<Request, Response>(request: IRequestParams<Request>): Promise<Response> {
    const fullUrl = request.url + this._objectToQuery(request.queryParams);
    return this._wrapRequest(() =>
      this._request<Request, Response>({
        method: HttpMethod.Put,
        url: fullUrl,
        data: request.data,
        headers: request.headers,
        signal: request.signal,
      }),
    );
  }

  protected static patch<Request, Response>(request: IRequestParams<Request>): Promise<Response> {
    const fullUrl = request.url + this._objectToQuery(request.queryParams);
    return this._wrapRequest(() =>
      this._request<Request, Response>({
        method: HttpMethod.Patch,
        url: fullUrl,
        data: request.data,
        headers: request.headers,
        signal: request.signal,
      }),
    );
  }

  protected static delete<Request, Response>(request: IRequestParams<Request>): Promise<Response> {
    const fullUrl = request.url + this._objectToQuery(request.queryParams);
    return this._wrapRequest(() =>
      this._request<Request, Response>({
        method: HttpMethod.Delete,
        url: fullUrl,
        data: request.data,
        headers: request.headers,
        signal: request.signal,
      }),
    );
  }

  private static _objectToQuery = (obj?: object) => {
    if (!obj) return '';

    return qs.stringify(obj, {
      addQueryPrefix: true,
      indices: false,
      allowDots: true,
      skipNulls: true,
    });
  };

  private static async _wrapRequest<Response>(handler: () => Promise<Response>) {
    try {
      const result = await handler();
      return result;
    } catch (error) {
      if (RestApiError.isRestApiError(error)) {
        if (IGNORED_STATUS_ERRORS.includes(error.status)) {
          error.message = '';
          return Promise.reject(error);
        }
      }
      throw error;
    }
  }

  private static async _request<Request, Response>(request: IBaseRequestParams<Request>): Promise<Response> {
    const requestId = crypto.randomUUID();
    const headers =
      request.data && request.data instanceof FormData
        ? { ...(request.headers || {}) }
        : ({
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'Request-Id': requestId,
            ...(request.headers || {}),
          } as Headers);
    if (!headers.Authorization && headers.Authorization !== '' && AuthUtils.authDataCached != null) {
      const accessToken = AuthUtils.authDataCached.accessToken;
      if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
    }

    const options = {
      method: request.method || 'GET',
      body: request.data && request.data instanceof FormData ? request.data : request.data ? JSON.stringify(request.data) : null,
      headers,
      signal: request.signal,
    };

    const response = await fetch(this.baseUrl + request.url, options);
    if (response.ok) {
      const contentType = response.headers.get('content-type');

      if (response.status === 204) return response.status as Response;
      if (contentType && contentType.indexOf('application/json') !== -1) return response.json();
      return {
        data: await response.arrayBuffer(),
        contentType: contentType,
        contentDisposition: response.headers.get('content-disposition'),
      } as Response;
    } else {
      const data = await response.json();
      const error = new RestApiError(data.message || 'Unknown error', response.status, data, request.method, request.url);
      if (!IGNORED_STATUS_ERRORS.includes(response.status)) {
        setContext('Request', {
          requestId,
          method: request.method,
          url: request.url,
          status: response.status,
          message: data.message || 'Unknown error',
        });
        setUser({ id: AuthUtils.getUserId() });
        captureException(error);
      }
      throw error;
    }
  }
}
